import React, {useMemo, useState} from 'react';

import {useGate, useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {generatePath} from 'react-router-dom';
import {toast} from 'react-toastify';

import {blockNkBranch, deleteNkBranch, unblockNkBranch} from 'api';
import {ReactComponent as SettingsIcon} from 'assets/svg/options.svg';
import {ReactComponent as TrashIcon} from 'assets/svg/trash.svg';
import {Page} from 'components/common/Page';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {Modal} from 'components/ui-kit/Modal';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {RestError, showRestError} from 'core/errors';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {branchesNkState$, fetchBranchesFx, Gate} from 'models/profile/branchesNk';
import {colors} from 'theme';
import {NkBranch} from 'types';
import {handleNil} from 'utils/handleNil';

import {ProfileNavigationTabs} from '../NavigationTabs';
import {
    ActionsContainer,
    Action,
    CreateUserButton,
    HeaderControls,
    UserStatusTag,
} from '../Staff/styled';
import {ConfirmBlockMessage, ConfirmRemoveFooter} from '../Staff/UpdateUser/styled';
import {Wrapper} from '../styled';

enum BranchesNkAccessor {
    name = 'name',
    location = 'location',
    status = 'status',
    employeeCount = 'employeeCount',
    actions = 'actions',
}

const getColumns = (t: TFunction) =>
    mapTableColumns([
        {id: BranchesNkAccessor.name, header: t('pages.profile.branchesNk.name')},
        {id: BranchesNkAccessor.location, header: t('pages.profile.branchesNk.location')},
        {id: BranchesNkAccessor.status, header: t('pages.profile.branchesNk.status'), width: 120},
        {
            id: BranchesNkAccessor.employeeCount,
            header: t('pages.profile.branchesNk.employeeCount'),
            width: 180,
        },
        {id: BranchesNkAccessor.actions, header: t('pages.profile.branchesNk.actions'), width: 180},
    ]);

const mapBranches = (
    branches: NkBranch[],
    t: TFunction,
    onChangeStatus: (branch: NkBranch) => void,
    onDelete: (branch: NkBranch) => void,
) =>
    branches.map((branch) => {
        const isActive = branch.isActive !== false;

        return {
            [BranchesNkAccessor.name]: handleNil(branch.name),
            [BranchesNkAccessor.location]: handleNil(
                [branch.region, branch.city].filter(Boolean).join(', ') || null,
            ),
            [BranchesNkAccessor.status]: (
                <UserStatusTag isActive={isActive}>
                    {t(`pages.profile.branchesNk.statuses.${isActive ? 'ACTIVE' : 'BLOCKED'}`)}
                </UserStatusTag>
            ),
            [BranchesNkAccessor.employeeCount]: branch.employeeCount ?? 0,
            [BranchesNkAccessor.actions]: (
                <ActionsContainer>
                    <Action
                        title={t('pages.profile.branchesNk.edit')}
                        onClick={() =>
                            history.push(generatePath(ROUTE.BRANCHES_NK_UPDATE, {id: branch.id}))
                        }
                    >
                        <Icon source={SettingsIcon} fillColor="#000" width={18} height={18} />
                    </Action>
                    <Action
                        disabled={Boolean(branch.employeeCount)}
                        title={
                            branch.employeeCount
                                ? t('pages.profile.branchesNk.deleteUnavailable')
                                : t('pages.profile.branchesNk.delete')
                        }
                        onClick={() => onDelete(branch)}
                    >
                        <Icon source={TrashIcon} fillColor={colors.danger} width={16} height={16} />
                    </Action>
                    <Action onClick={() => onChangeStatus(branch)}>
                        {t(`pages.profile.branchesNk.${isActive ? 'block' : 'unblock'}`)}
                    </Action>
                </ActionsContainer>
            ),
        };
    });

export const BranchesNk = () => {
    const {t} = useTranslation();
    const state = useStore(branchesNkState$);
    const [branchToChangeStatus, setBranchToChangeStatus] = useState<NkBranch | null>(null);
    const [branchToDelete, setBranchToDelete] = useState<NkBranch | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    useGate(Gate);

    const columns = useMemo(() => getColumns(t), [t]);
    const data = useMemo(
        () => mapBranches(state.branches, t, setBranchToChangeStatus, setBranchToDelete),
        [state.branches, t],
    );
    const selectedBranch = branchToChangeStatus || branchToDelete;
    const closeModal = () => {
        if (isSubmitting) return;
        setBranchToChangeStatus(null);
        setBranchToDelete(null);
    };
    const submitAction = async () => {
        if (!selectedBranch) return;
        setIsSubmitting(true);
        try {
            if (branchToDelete) {
                await deleteNkBranch(selectedBranch.id);
                toast.success(t('pages.profile.branchesNk.deleteSuccess'));
            } else if (selectedBranch.isActive === false) {
                await unblockNkBranch(selectedBranch.id);
                toast.success(t('pages.profile.branchesNk.unblockSuccess'));
            } else {
                await blockNkBranch(selectedBranch.id);
                toast.success(t('pages.profile.branchesNk.blockSuccess'));
            }
            await fetchBranchesFx();
            setBranchToChangeStatus(null);
            setBranchToDelete(null);
        } catch (error) {
            showRestError(error as RestError);
        } finally {
            setIsSubmitting(false);
        }
    };
    const headerControls = (
        <HeaderControls>
            <CreateUserButton size="sm" onClick={() => history.push(ROUTE.BRANCHES_NK_CREATE)}>
                + {t('pages.profile.branchesNk.create')}
            </CreateUserButton>
        </HeaderControls>
    );

    return (
        <Page title={t('pages.profile.title')}>
            <Wrapper>
                <ProfileNavigationTabs />
                <ContentContainer noPaddings>
                    <Table
                        columns={columns}
                        data={data}
                        totalRowsCount={data.length}
                        isLoading={state.isLoading}
                        headerControls={headerControls}
                        hideSetting
                        hasFooter={false}
                    />
                </ContentContainer>
            </Wrapper>
            <Modal
                title={t(
                    `pages.profile.branchesNk.confirm.${
                        branchToDelete
                            ? 'deleteTitle'
                            : branchToChangeStatus?.isActive === false
                            ? 'unblockTitle'
                            : 'blockTitle'
                    }`,
                )}
                modalWidth={0}
                show={Boolean(selectedBranch)}
                onClose={closeModal}
                closable={!isSubmitting}
            >
                {selectedBranch && (
                    <>
                        <ConfirmBlockMessage>
                            {t(
                                `pages.profile.branchesNk.confirm.${
                                    branchToDelete
                                        ? 'deleteMessage'
                                        : branchToChangeStatus?.isActive === false
                                        ? 'unblockMessage'
                                        : 'blockMessage'
                                }`,
                                {name: selectedBranch.name},
                            )}
                        </ConfirmBlockMessage>
                        <ConfirmRemoveFooter>
                            <Button
                                type="button"
                                variant={ButtonVariant.Outline}
                                disabled={isSubmitting}
                                onClick={closeModal}
                            >
                                {t('pages.profile.branchesNk.form.cancel')}
                            </Button>
                            <Button type="button" isLoading={isSubmitting} onClick={submitAction}>
                                {t('pages.profile.branchesNk.confirm.confirm')}
                            </Button>
                        </ConfirmRemoveFooter>
                    </>
                )}
            </Modal>
        </Page>
    );
};
